import { Vehicle } from '@/types';

/**
 * Génère un slug SEO-friendly pour un véhicule
 * Format: marque-modele-annee-id
 * Exemple: mercedes-benz-actros-1851-2018-abc123
 */
export const generateSlug = (vehicle: Vehicle): string => {
  const parts: string[] = [];
  
  // Ajouter la marque
  if (vehicle.brand) {
    parts.push(slugify(vehicle.brand));
  }
  
  // Extraire le modèle du titre si pas de marque
  if (!vehicle.brand && vehicle.title) {
    const titleParts = vehicle.title.split(' ').slice(0, 3);
    parts.push(...titleParts.map(slugify));
  } else if (vehicle.title) {
    // Essayer d'extraire le modèle après la marque
    const titleWithoutBrand = vehicle.brand 
      ? vehicle.title.toLowerCase().replace(vehicle.brand.toLowerCase(), '').trim()
      : vehicle.title;
    const modelParts = titleWithoutBrand.split(' ').slice(0, 2);
    parts.push(...modelParts.map(slugify));
  }
  
  // Ajouter l'année
  if (vehicle.year && vehicle.year !== 'avant-1990') {
    parts.push(vehicle.year.toString());
  }
  
  // Ajouter l'ID à la fin sans les tirets (permet de retrouver le véhicule)
  parts.push(vehicle.id.replace(/-/g, '').slice(0, 8));
  
  return parts.filter(Boolean).join('-').toLowerCase();
};

/**
 * Parse un slug pour extraire l'ID du véhicule
 * Le slug contient toujours l'ID en dernier (8 caractères)
 */
export const parseSlug = (slug: string): string | null => {
  const parts = slug.split('-');
  if (parts.length === 0) return null;
  
  // L'ID est toujours le dernier élément (8 caractères)
  const potentialId = parts[parts.length - 1];
  
  // Vérifier que c'est un ID valide (8 caractères hexadécimaux)
  if (potentialId && /^[a-f0-9]{8}$/i.test(potentialId)) {
    return potentialId;
  }
  
  return null;
};

/**
 * Transforme une chaîne en slug
 */
const slugify = (text: string): string => {
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD') // Décompose les caractères accentués
    .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
    .replace(/[^\w\s-]/g, '') // Supprime les caractères spéciaux
    .replace(/\s+/g, '-') // Remplace les espaces par des tirets
    .replace(/--+/g, '-') // Remplace les doubles tirets
    .replace(/^-+/, '') // Supprime les tirets en début
    .replace(/-+$/, ''); // Supprime les tirets en fin
};

/**
 * Génère l'URL complète d'un véhicule
 */
export const getVehicleUrl = (vehicle: Vehicle, baseUrl: string = ''): string => {
  const slug = generateSlug(vehicle);
  return `${baseUrl}/vehicule/${slug}`;
};

/**
 * Génère les métadonnées SEO pour un véhicule
 */
export const getVehicleSeoTitle = (vehicle: Vehicle): string => {
  const parts: string[] = [];
  
  if (vehicle.brand) parts.push(vehicle.brand);
  
  // Extraire modèle du titre
  const titleWithoutBrand = vehicle.brand 
    ? vehicle.title.replace(new RegExp(vehicle.brand, 'i'), '').trim()
    : vehicle.title;
  
  if (titleWithoutBrand) {
    parts.push(titleWithoutBrand.split(' ').slice(0, 3).join(' '));
  }
  
  if (vehicle.year && vehicle.year !== 'avant-1990') {
    parts.push(vehicle.year.toString());
  }
  
  if (vehicle.mileage) {
    parts.push(`${Math.round(vehicle.mileage / 1000)}k km`);
  }
  
  parts.push('| Trucks 52 SARL');
  
  return parts.join(' - ');
};

/**
 * Génère la meta description SEO pour un véhicule
 */
export const getVehicleSeoDescription = (vehicle: Vehicle): string => {
  const parts: string[] = [];
  
  parts.push(`${vehicle.title} d'occasion`);
  
  if (vehicle.year && vehicle.year !== 'avant-1990') {
    parts.push(`de ${vehicle.year}`);
  }
  
  if (vehicle.mileage) {
    parts.push(`avec ${vehicle.mileage.toLocaleString('fr-FR')} km`);
  }
  
  if (vehicle.euroStandard) {
    parts.push(`Norme ${vehicle.euroStandard}`);
  }
  
  if (vehicle.price || vehicle.priceTTC) {
    const price = vehicle.priceTTC || vehicle.price;
    parts.push(`Prix: ${price.toLocaleString('fr-FR')} €`);
  }
  
  parts.push('Disponible chez Trucks 52 SARL à Val-de-Meuse, Haute-Marne.');
  
  return parts.join('. ').slice(0, 160);
};
